import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  segments,
  getSegmentById,
  getCategoriesForSegment,
  getSubcategoriesForCategory,
  deriveSelectionFromSubcategories,
  type CategoryNode,
} from "@/constants/categoryTaxonomy";

type Step = 1 | 2 | 3;

interface CategoryPickerProps {
  /** Current saved subcategory IDs (used to seed the picker). */
  initial?: string[];
  /** Called when user confirms the selection. */
  onSubmit: (subcategoryIds: string[]) => void | Promise<void>;
  /** Called when the user dismisses the picker (settings dialog). Optional. */
  onCancel?: () => void;
  /** Hide cancel/back-out controls — used in onboarding where this is the only path forward. */
  hideCancel?: boolean;
  isSaving?: boolean;
  submitLabel?: string;
  /**
   * Label for the cancel button shown on the segment step. Defaults to
   * "Anuluj" (closes a settings sheet); pass "Wstecz" when this picker is
   * embedded in a multi-step flow where dismissing means "go back to the
   * previous step", not "discard".
   */
  cancelLabel?: string;
}

const stepLabels: Record<Step, string> = {
  1: "Segment",
  2: "Kategorie",
  3: "Podkategorie",
};

const stepHeadings: Record<Step, { title: string; subtitle: string }> = {
  1: {
    title: "Kim jesteś na Instagramie?",
    subtitle: "Wybierz jeden segment, który najlepiej do Ciebie pasuje.",
  },
  2: {
    title: "Jakie obszary tworzysz?",
    subtitle: "Zaznacz kategorie, w których publikujesz — możesz wybrać kilka.",
  },
  3: {
    title: "Doprecyzuj nisze",
    subtitle: "Wybierz konkretne tematy. 3–5 to słodki strzał, ale nie ma limitu.",
  },
};

export function CategoryPicker({
  initial = [],
  onSubmit,
  onCancel,
  hideCancel = false,
  isSaving = false,
  submitLabel = "Zapisz",
  cancelLabel = "Anuluj",
}: CategoryPickerProps) {
  // Seed wizard state from saved selection.
  const seed = useMemo(() => deriveSelectionFromSubcategories(initial), [initial]);

  const [step, setStep] = useState<Step>(seed.segmentId ? 3 : 1);
  const [segmentId, setSegmentId] = useState<string | null>(seed.segmentId);
  const [categoryIds, setCategoryIds] = useState<string[]>(seed.categoryIds);
  const [subcategoryIds, setSubcategoryIds] = useState<string[]>(seed.subcategoryIds);
  const [search, setSearch] = useState("");

  // Reset search when leaving step 3.
  useEffect(() => {
    if (step !== 3) setSearch("");
  }, [step]);

  const selectedSegment = segmentId ? getSegmentById(segmentId) : null;
  const availableCategories = segmentId ? getCategoriesForSegment(segmentId) : [];

  const handleSelectSegment = (id: string) => {
    if (id !== segmentId) {
      setSegmentId(id);
      setCategoryIds([]);
      setSubcategoryIds([]);
    }
    setStep(2);
  };

  const toggleCategory = (id: string) => {
    setCategoryIds((prev) => {
      if (prev.includes(id)) {
        // Deselecting a category drops its subcategories too.
        const subsInCategory = getSubcategoriesForCategory(id).map((s) => s.id);
        setSubcategoryIds((s) => s.filter((sid) => !subsInCategory.includes(sid)));
        return prev.filter((c) => c !== id);
      }
      return [...prev, id];
    });
  };

  const toggleSubcategory = (id: string) => {
    setSubcategoryIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const canAdvanceFromStep2 = categoryIds.length > 0;
  const canSubmit = subcategoryIds.length > 0 && !isSaving;

  const handleBack = () => {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
    else onCancel?.();
  };

  const handleNext = () => {
    if (step === 1) setStep(2);
    else if (step === 2 && canAdvanceFromStep2) setStep(3);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(subcategoryIds);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Internal step indicator — minimalist line + label */}
      <div className="flex items-center gap-2 text-xs font-medium tracking-wide uppercase text-gray-500">
        {([1, 2, 3] as Step[]).map((s, idx) => (
          <div key={s} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold transition-colors",
                step === s
                  ? "bg-primary text-primary-foreground"
                  : step > s
                  ? "bg-primary/15 text-primary"
                  : "bg-gray-200 text-gray-400"
              )}
            >
              {step > s ? <Check className="h-3 w-3" /> : s}
            </span>
            <span className={cn(step === s ? "text-gray-900" : "text-gray-400")}>
              {stepLabels[s]}
            </span>
            {idx < 2 && <span className="mx-1 h-px w-5 bg-gray-300" />}
          </div>
        ))}
      </div>

      {/* Heading */}
      <div>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-gray-900 leading-tight">
          {stepHeadings[step].title}
        </h2>
        <p className="mt-1 text-sm text-gray-500">{stepHeadings[step].subtitle}</p>
      </div>

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
        >
          {step === 1 && (
            <SegmentGrid
              currentSegmentId={segmentId}
              onSelect={handleSelectSegment}
            />
          )}

          {step === 2 && selectedSegment && (
            <CategoriesList
              categories={availableCategories}
              selected={categoryIds}
              onToggle={toggleCategory}
            />
          )}

          {step === 3 && selectedSegment && (
            <SubcategoriesGrouped
              categories={availableCategories.filter((c) =>
                categoryIds.includes(c.id)
              )}
              selected={subcategoryIds}
              onToggle={toggleSubcategory}
              search={search}
              onSearchChange={setSearch}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer actions */}
      <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          disabled={isSaving || (step === 1 && hideCancel)}
          className="text-gray-500 hover:text-gray-900 sm:w-auto"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {step === 1 ? (onCancel ? cancelLabel : "Wstecz") : "Wstecz"}
        </Button>

        {step < 3 ? (
          <Button
            type="button"
            onClick={handleNext}
            disabled={step === 2 && !canAdvanceFromStep2}
            className="sm:w-auto"
          >
            Dalej
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="sm:w-auto"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Zapisywanie…
              </>
            ) : (
              <>
                {submitLabel}
                {subcategoryIds.length > 0 && (
                  <span className="ml-2 rounded-full bg-primary-foreground/15 px-2 py-0.5 text-xs">
                    {subcategoryIds.length}
                  </span>
                )}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────── Step 1 ────────────────────────────────

function SegmentGrid({
  currentSegmentId,
  onSelect,
}: {
  currentSegmentId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {segments.map((segment) => {
        const active = segment.id === currentSegmentId;
        return (
          <button
            key={segment.id}
            type="button"
            onClick={() => onSelect(segment.id)}
            className={cn(
              "group relative flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-all",
              "hover:border-primary/60 hover:bg-primary/5",
              active
                ? "border-primary bg-primary/5 shadow-sm"
                : "border-gray-200 bg-white"
            )}
          >
            <span className="text-3xl leading-none">{segment.emoji}</span>
            <span className="text-base font-semibold text-gray-900">
              {segment.name}
            </span>
            <span className="text-xs text-gray-500 leading-snug">
              {segment.hint}
            </span>
            {active && (
              <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────────── Step 2 ────────────────────────────────

function CategoriesList({
  categories,
  selected,
  onToggle,
}: {
  categories: CategoryNode[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {categories.map((cat) => {
        const active = selected.includes(cat.id);
        return (
          <button
            key={cat.id}
            type="button"
            onClick={() => onToggle(cat.id)}
            className={cn(
              "relative flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-all",
              "hover:border-primary/60 hover:bg-primary/5",
              active
                ? "border-primary bg-primary/5"
                : "border-gray-200 bg-white"
            )}
          >
            <span className="text-xl leading-none">{cat.emoji}</span>
            <span className="text-sm font-medium leading-snug text-gray-900">
              {cat.name}
            </span>
            {active && (
              <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────────── Step 3 ────────────────────────────────

function SubcategoriesGrouped({
  categories,
  selected,
  onToggle,
  search,
  onSearchChange,
}: {
  categories: CategoryNode[];
  selected: string[];
  onToggle: (id: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
}) {
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = categories.map((cat) => ({
    ...cat,
    subcategories: getSubcategoriesForCategory(cat.id).filter((s) =>
      normalizedSearch ? s.name.toLowerCase().includes(normalizedSearch) : true
    ),
  }));

  const totalAfterFilter = filtered.reduce(
    (sum, cat) => sum + cat.subcategories.length,
    0
  );

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Szukaj podkategorii…"
          className="pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
            aria-label="Wyczyść wyszukiwanie"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Empty state */}
      {totalAfterFilter === 0 && (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-6 text-center text-sm text-gray-500">
          Brak wyników dla „{search}".
        </div>
      )}

      {/* Grouped chips */}
      <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
        {filtered.map((cat) => {
          if (cat.subcategories.length === 0) return null;
          return (
            <div key={cat.id}>
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span>{cat.emoji}</span>
                <span>{cat.name}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cat.subcategories.map((sub) => {
                  const active = selected.includes(sub.id);
                  return (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => onToggle(sub.id)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all",
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-gray-200 bg-white text-gray-700 hover:border-primary/60 hover:bg-primary/5"
                      )}
                    >
                      {active && <Check className="h-3 w-3" />}
                      {sub.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
